import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  useTheme,
  useMediaQuery
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import SpaIcon from '@mui/icons-material/Spa';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterVintageIcon from '@mui/icons-material/FilterVintage';
import CloudIcon from '@mui/icons-material/Cloud';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import mapImage from '../assets/map.projects.png';

// Impact statistics interface
type ImpactStat = {
  icon: React.ReactNode;
  value: number | string;
  label: string;
};

// Supported project interface
type Project = {
  id: number;
  title: string;
  category: string;
  location: string;
  metric: string;
  imgUrl: string;
};

// Story interface with full details
type Story = {
  id: number;
  title: string;
  location: string;
  date: string;
  description: string;
  imgUrl: string;
};

const ImpactDashboard: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const impactStats: ImpactStat[] = [
    { icon: <SpaIcon fontSize="large" color="primary" />, value: 149, label: 'Mangrove trees restored' },
    { icon: <MenuBookIcon fontSize="large" color="primary" />, value: 1215, label: 'Lessons provided' },
    { icon: <DeleteIcon fontSize="large" color="primary" />, value: 674, label: 'Kg of plastic collected' },
    { icon: <CloudIcon fontSize="large" color="primary" />, value: '149,632', label: 'kgCO₂e to be absorbed' },
    { icon: <SchoolIcon fontSize="large" color="primary" />, value: 151, label: 'Days of education' },
    { icon: <AccessibilityNewIcon fontSize="large" color="primary" />, value: '33,700', label: 'Plastic bottles collected ' },
  ];

  const projects: Project[] = [
    {
      id: 1,
      title: 'Restoring mangroves with Yakopi',
      category: 'Mangrove restoration',
      location: 'Indonesia',
      metric: 'Protect and restore degraded coastal ecosystems in Aceh Timur, where YAKOPI will plant more than 460,000 mangrove trees across ~104 hectares.',
      imgUrl: 'https://handprint-live.s3.ap-southeast-1.amazonaws.com/uploads/raas/Partners/YAKOPI/Aceh+Timur/YAK-worker_boat_mangroves_Aceh-0624(2)+(1).avif'
    },
    {
      id: 2,
      title: 'Educate youths with Think Equal',
      category: 'Education',
      location: 'Colombia',
      metric: 'Provide educational kits comprised of teachers\' training programs, games, books... to implement Social and Emotional Learning (SEL), which fosters inclusion, equality and wellbeing.',
      imgUrl: 'https://handprint-live.s3.ap-southeast-1.amazonaws.com/uploads/raas/Partners/ThinkEqual/Colombia/TEQ-cover.avif'
    },
    {
      id: 3,
      title: 'Cleaning up ocean plastic with Plastic Bank',
      category: 'Ocean plastic clean up',
      location: 'Egypt',
      metric: 'Empower communities in Egypt to earn an income and receive healthcare benefits by trading the plastic waste they collect through a recycling program.',
      imgUrl: 'https://handprint-live.s3.ap-southeast-1.amazonaws.com/uploads/raas/Partners/PlasticBank/Egypt/Plastic+Bank+Manshiyet+Nasser+Shoot+(155).avif'
    }
  ];

  const stories: Story[] = [
    {
      id: 1,
      title: 'Mangrove - Mangrove Planting Day',
      location: 'Aceh Timur, Indonesia',
      date: 'June 24, 2025',
      description: 'From the selection of mangrove seedlings in nurseries to their transport via boats, community members—young and old—work together at the plantation site, planting the seedlings along the water’s edge to protect the coastline from erosion and restore critical habitats.',
      imgUrl: 'https://handprint-live.s3.ap-southeast-1.amazonaws.com/uploads/raas/Partners/YAKOPI/Aceh+Timur/YAK-worker_boat_mangroves_Aceh-0624(2)+(1).avif'
    },
    {
      id: 2,
      title: 'Education - New Classroom in Colombia',
      location: 'Cauca, Colombia',
      date: 'March 15, 2025',
      description: 'Local families collaborated to build a new classroom, ensuring children in Colombia have a safe, green space to learn and grow.',
      imgUrl: 'https://handprint-live.s3.amazonaws.com/uploads/raas/Partners/ThinkEqual/Colombia/TEQ-CO-Cover2.avif'
    },
    {
      id: 3,
      title: 'Plastic - Beach Cleanup Initiative',
      location: 'Port Said, Egypt',
      date: 'March 5, 2025',
      description: 'Volunteers gathered early in the morning to remove plastic waste from the shoreline, collecting over 200 kg of debris and preventing it from entering the ocean ecosystem.',
      imgUrl: 'https://handprint-live.s3.amazonaws.com/uploads/raas/Partners/PlasticBank/Egypt/Stories/Plastic-Bank-Portsaid-Beach-Shoot-_45_.avif'
    },
    {
      id: 6,
      title: 'Mangrove - How we restore mangroves',
      location: 'Aceh Timur, Indonesia',
      date: 'Feb 6, 2025',
      description: 'We grow seedlings in a nursery. Later on propagules are getting ready for planting. We build stakes for support. Propagules and stakes are loaded onto boats, carried to the plantation site in Aceh, Indonesia. The mangroves are planted in line, and we can see signs of biodiversity in the restoration area, with purple heron or good old crabs!',
      imgUrl: 'https://handprint-live.s3.amazonaws.com/uploads/raas/Partners/YAKOPI/Aceh+Timur/Stories/YAK-worker_holds_propagules_Aceh-0624(3)+(1).avif'
    },
    {
      id: 7,
      title: 'Mangrove - How we restore mangroves',
      location: 'Probolingo, Indonesia',
      date: '2021–2024',
      description: 'Yagasu’s mangrove restoration effort in Probolingo, Indonesia, captures the transformation from a damaged coastline to a thriving ecosystem. In 2021, deforestation and erosion had left the area nearly bare, but local community efforts began to change that. By 2023 and 2024, healthy mangrove forests emerged, with roots stabilizing the soil and protecting the coast from erosion.',
      imgUrl: 'https://handprint-live.s3.amazonaws.com/uploads/raas/Partners/Yagasu/Situbondo/Stories/YAG-Community.avif'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Green page title */}
      <Typography variant="h3" sx={{ color: 'rgb(79,111,82)', mb: 1 }}>
        Regenerative Feed
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 4 }}>
        Engage customers with a live dashboard of your regenerative actions and achievements, showcasing real-time impact metrics.
      </Typography>

      {/* Our impact section */}
      <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700 }}>
        Our impact
      </Typography>
      <Box sx={{ p: 4, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4, mb: 6, backgroundColor: 'white' }}>
        {impactStats.map((stat, idx) => (
          <Box key={idx} sx={{ width: { xs: '50%', sm: '33.333%', md: '25%' }, textAlign: 'center' }}>
            {stat.icon}
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
              {stat.value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Projects we support */}
      <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700 }}>
        The projects we support
      </Typography>
      <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4, px: 4 }}>
        Rather than doing solely carbon offsetting like other companies of our industry, we assessed the impact of our operations on nature as a whole. That’s how we defined our portfolio of impact projects, supporting the local communities nearby our key supply chain hubs, and regenerating natural ecosystems focusing on biodiversity conservation, plastic removal and education.
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4, mb: 6 }}>
        {projects.map((proj) => (
          <Box key={proj.id} sx={{ width: { xs: '100%', sm: '48%', md: '31%' } }}>
            <Card sx={{ height: '100%' }}>
              <CardMedia component="img" height="160" image={proj.imgUrl} alt={proj.title} />
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  {proj.category}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, my: 1 }}>
                  {proj.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {proj.location}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 400 }}>
                  {proj.metric}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Impact stories section */}
      <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700 }}>
        Impact Stories
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4, mb: 6 }}>
        {stories.map((story) => (
          <Box key={story.id} sx={{ width: { xs: '100%', sm: '48%', md: '31%' } }}>
            <Card>
              <CardMedia component="img" height="200" image={story.imgUrl} alt={story.title} />
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {story.title}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <strong>{story.location} — {story.date}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {story.description}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Project locations map */}
                                            <Typography
  variant="h4"
  align="center"
  gutterBottom
  sx={{ fontWeight: 700, mb: 2 }}
>
  Project locations
</Typography>
<Box
  component="img"
  src={mapImage}
  alt="Project map"
  sx={{
    display: 'block',      // make it a block so mx works
    mx: 'auto',            // center horizontally
    maxWidth: '80%',       // never exceed 80% of container
    width: 'auto',         // use intrinsic size up to maxWidth
    height: 'auto',        // maintain aspect ratio
    borderRadius: 1,
    mb: 2,
  }}
/>

    </Container>
    
  );
};

export default ImpactDashboard;